import IcDropdownChecked from "@/assets/menu/ic_dropdown_checked";
import { getNetworkImageSrc } from "@/constants";
import { sendTransactionHash } from "@/services/api";
import { AuthContext } from "@/services/AuthContext";
import { Modal } from "flowbite-react";
import { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { TiTick } from "react-icons/ti";
import Web3 from "web3";
import Loader from "./Loader";
import Button from "./UI/Button";
import USDC_ABI from "./usdc_abi.json";
import USDCLogo from "@/assets/usd-coin-usdc-logo.png";

const POL_USDC_ADDRESS = "0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359";
// const POL_ADDRESS = "0x0000000000000000000000000000000000001010";
// const PEAQ_USDC_ADDRESS = "0x3c499c542cEF5E3811e1192ce70d8cC03d5c3355";
// const PEAQ_ADDRESS = "0x0000000000000000000000000000000000001010"; // Assuming this is the native PEAQ token address

const WithdrawWallet = ({ closeModal, networkType }: any) => {
  const { provider } = useContext(AuthContext);
  const [selectedToken, setSelectedToken] = useState<any>(null);
  const [usdcBalance, setUsdcBalance] = useState("0.0");
  // eslint-disable-next-line
  const [nativeBalance, setNativeBalance] = useState("0.0");
  const [amount, setAmount] = useState("");
  const [destinationAddress, setDestinationAddress] = useState("");
  const [transactionHash, setTransactionHash] = useState("");
  const [isTransferring, setIsTransferring] = useState(false);

  useEffect(() => {
    if (provider) {
      const web3 = new Web3(provider);
      const fetchBalances = async () => {
        try {
          const accounts = await web3.eth.getAccounts();
          if (accounts.length === 0) return;

          const account = accounts[0];

          if (networkType === "polygon") {
            const usdcContract = new web3.eth.Contract(USDC_ABI, POL_USDC_ADDRESS);
            const usdcBalance = await usdcContract.methods.balanceOf(account).call();
            const formattedUSDCBalance = web3.utils.fromWei(usdcBalance, "mwei");
            setUsdcBalance(formattedUSDCBalance);
            setSelectedToken({ symbol: "USDC", balance: formattedUSDCBalance });
          } else if (networkType === "peaq") {
            setSelectedToken({ symbol: "USDC", balance: usdcBalance });
          }

          const nativeBalance = await web3.eth.getBalance(account);
          const formattedNativeBalance = web3.utils.fromWei(nativeBalance, "ether");
          setNativeBalance(formattedNativeBalance);

          if (networkType === "peaq") {
            // setSelectedToken({ symbol: "PEAQ", balance: formattedNativeBalance });
            setSelectedToken({ symbol: "USDC", balance: usdcBalance });
          }
        } catch (error) {
          toast.error("Error Fetching Balances");
        }
      };

      fetchBalances();
    }
    // eslint-disable-next-line
  }, [provider, networkType]);

  const handleTransfer = async () => {
    if (!provider || !selectedToken) return;
    setIsTransferring(true);
    const web3 = new Web3(provider);

    try {
      const accounts = await web3.eth.getAccounts();
      const account = accounts[0];

      if (parseFloat(amount) > parseFloat(selectedToken.balance)) {
        toast.error("Insufficient balance");
        setIsTransferring(false);
        return;
      }

      const amountString = amount.trim();
      if (isNaN(Number(amountString)) || Number(amountString) <= 0) {
        toast.error("Invalid amount");
        setIsTransferring(false);
        return;
      }

      const currentGasPrice = await web3.eth.getGasPrice();

      if (networkType === "polygon" && selectedToken.symbol === "USDC") {
        const usdcContract = new web3.eth.Contract(USDC_ABI, POL_USDC_ADDRESS);
        const amountInMwei = web3.utils.toWei(amountString, "mwei");

        const estimatedGas = await usdcContract.methods.transfer(destinationAddress, amountInMwei).estimateGas({ from: account });

        const txReceipt = await usdcContract.methods.transfer(destinationAddress, amountInMwei).send({
          from: account,
          gasPrice: currentGasPrice,
          gas: estimatedGas,
        });

        setTransactionHash(txReceipt.transactionHash);

        // Send transaction details to backend using the existing sendTransactionHash function
        const transactionData = {
          transactionPurpose: account === destinationAddress ? "PAYMENT_TOKEN_TOP_UP" : "PAYMENT_TOKEN_WITHDRAW",
          transactionMethod: "ON_CHAIN",
          transactionHash: txReceipt.transactionHash,
          fromAddress: account,
          toAddress: destinationAddress,
          paymentTokenName: selectedToken.symbol,
          paymentTokensTransferred: parseFloat(amount),
          networkType,
        };

        await sendTransactionHash(transactionData);
      } else {
        const amountInEther = web3.utils.toWei(amountString, "ether");

        const estimatedGas = await web3.eth.estimateGas({
          from: account,
          to: destinationAddress,
          value: amountInEther,
        });

        const txReceipt = await web3.eth.sendTransaction({
          from: account,
          to: destinationAddress,
          value: amountInEther,
          gasPrice: currentGasPrice,
          gas: estimatedGas,
        });

        setTransactionHash(txReceipt.transactionHash);

        // Send transaction details to backend using the existing sendTransactionHash function
        const transactionData = {
          transactionPurpose: account === destinationAddress ? "PAYMENT_TOKEN_TOP_UP" : "PAYMENT_TOKEN_WITHDRAW",
          transactionMethod: "ON_CHAIN",
          transactionHash: txReceipt.transactionHash,
          fromAddress: account,
          toAddress: destinationAddress,
          paymentTokenName: selectedToken.symbol,
          paymentTokensTransferred: parseFloat(amount),
          networkType,
        };

        await sendTransactionHash(transactionData);
        // await sendTransactionHash(account,destinationAddress,txReceipt.transactionHash)
      }

      toast.success("Transaction successful. You can check it on the explorer.");
    } catch (error) {
      toast.error("Transaction failed: " + error?.message);
    } finally {
      setIsTransferring(false);
    }
  };

  const explorerLink =
    networkType === "polygon"
      ? `${process.env.REACT_APP_POLYGONSCAN_TRANSACTION_URL}${transactionHash}`
      : `${process.env.REACT_APP_PEAQ_SUBSCAN_TRANSACTION_URL}${transactionHash}`;

  // eslint-disable-next-line
  const nativeTokenSymbol = networkType === "polygon" ? "POL" : "PEAQ";

  useEffect(() => {
    setSelectedToken({ symbol: "USDC", balance: usdcBalance });
    // eslint-disable-next-line
  }, []);

  return (
    <Modal show={true} onClose={closeModal} data-bs-theme="dark" position={"center"} data-testid="withdraw-wallet-modal">
      <Modal.Body className="p-6 bg-white text-black flex flex-col gap-3">
        <div className="flex flex-row justify-between ">
          <h3 className="flex text-black text-xl font-semibold">Withdraw</h3>
          <img src="./assets/modalCross.svg" alt="cross" onClick={closeModal} className="flex cursor-pointer" />
        </div>

        <hr className="w-full border-t border-[#dad1d1]" />

        {!transactionHash && (
          <>
            <div className="mb-4">
              <h4 className="mb-2 text-sm">Select Token</h4>
              <div className="flex flex-row border-[#dad1d1] border-b-[1px]">
                <Button
                  primary={selectedToken && selectedToken.symbol === "USDC"}
                  onClick={() => networkType === "polygon" && setSelectedToken({ symbol: "USDC", balance: usdcBalance })}
                  classNames={`flex w-full border-none `}
                  // classNames={`flex w-full border-none ${networkType === "peaq" ? "opacity-50 cursor-not-allowed" : ""}`}
                  // disabled={networkType === "peaq"}
                >
                  <span className="flex flex-row items-center justify-between w-full">
                    <div className="flex flex-row gap-2 items-center">
                      <img src={USDCLogo} alt="USDC icon" className="flex w-4 h-4" />
                      USDC
                    </div>
                    {selectedToken?.symbol === "USDC" && <TiTick className="text-primary-100" />}
                  </span>
                </Button>
                {/* 
                <Button
                  primary={selectedToken && selectedToken.symbol === nativeTokenSymbol}
                  onClick={() => setSelectedToken({ symbol: nativeTokenSymbol, balance: nativeBalance })}
                  classNames="flex w-full border-none"
                >
                  <span className="flex flex-row items-center justify-between w-full">
                    <div className="flex flex-row gap-2 items-center">
                      <img
                        src={networkType === "polygon" ? "./assets/ic_polygon.svg" : "./assets/ic_peaq.svg"}
                        alt="currency icon"
                        className="flex w-4 h-4"
                      />
                      {nativeTokenSymbol}
                    </div>
                    {selectedToken?.symbol === nativeTokenSymbol && <TiTick className="text-primary-100" />}
                  </span>
                </Button> */}
              </div>
            </div>

            {selectedToken && (
              <div className="flex flex-col gap-4 w-full">
                <h5>Amount</h5>

                <div className="flex flex-row justify-between w-full p-4 rounded-lg border-[#dad1d1] border-[1px] ">
                  <div className="flex flex-col justify-between gap-6">
                    <span className="text-[12px] text-xs text-monochrome-20">
                      Balance : {selectedToken.balance} {selectedToken.symbol}
                    </span>
                    <input
                      placeholder={`Amount (${selectedToken.symbol})`}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      disabled={isTransferring}
                      className="bg-transparent text-sm focus:outline-none font-medium"
                    />
                    {/* <span className="text-xs text-monochrome-20">200 USD</span> */}
                  </div>
                  <div className="flex flex-col self-center">
                    <div className="flex flex-row gap-2 items-center rounded-lg border-[#dad1d1] border-[1px] p-3 ">
                      <img src={getNetworkImageSrc(selectedToken.symbol)} alt="currency icon" className="flex w-4 h-4" />
                      {selectedToken.symbol}
                    </div>
                  </div>
                </div>

                <h6>Destination Address</h6>

                <input
                  className="p-4 text-xs focus:outline-none rounded-lg border-[#dad1d1] border-[1px] bg-transparent"
                  placeholder="Enter your MetaMask wallet address"
                  value={destinationAddress}
                  onChange={(e) => setDestinationAddress(e.target.value)}
                  disabled={isTransferring}
                />

                <div className="flex flex-col w-full justify-end md:flex-row gap-2 self-center md:self-end ">
                  <Button onClick={closeModal} primary classNames="flex rounded-lg">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleTransfer}
                    disabled={!amount || !destinationAddress || isTransferring}
                    secondary
                    classNames="flex rounded-lg text-black"
                  >
                    {isTransferring ? (
                      <div className="flex items-center justify-center">
                        <Loader isLoading={true} />
                      </div>
                    ) : (
                      "Withdraw"
                    )}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {transactionHash && (
          <section className="p-4 flex flex-col gap-7 items-center">
            <IcDropdownChecked />
            <div className="flex flex-col gap-2 items-center">
              <h3>Withdrawal Successful</h3>
              <p className="text-sm w-[90%] text-center text-monochrome-20">You can view the transaction details on the block explorer</p>
            </div>
            <div className="flex flex-col md:flex-row gap-2 w-full ">
              <Button primary rounded onClick={() => window.open(explorerLink, "_blank")} classNames="w-full">
                View Transaction
              </Button>
              <Button primary rounded onClick={closeModal} classNames="w-full">
                Close
              </Button>
            </div>
          </section>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default WithdrawWallet;
